import React, { useEffect, useState, FC } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { getPageData, useData } from "../../../app/helper/functions";
import ButtonComponent from "../../atoms/Button/Button";
import YELLOWARROW from "../../../app/assets/images/yellow-arrow.svg";
import ELLIPSE from "../../../app/assets/images/homepage/ellipse_image.png";
import Heading from "../../atoms/Heading/Heading";
import RightArrow from "../../../app/assets/images/rightArrowBlue.svg";

interface ProjectDisplayProps {
  data?: {
    title?: string;
    subTitle?: string;
    text?: string;
  };
}

const ProjectDisplay: FC<ProjectDisplayProps> = ({ data }) => {
  const { loading, data: contextData, error } = useData();
  const projectData = getPageData(contextData, "projects");
  const [visibleCount, setVisibleCount] = useState(4);
  const navigate = useNavigate();

  const headingTitle = data?.title || "OUR WORK";
  const headingSubTitle = data?.subTitle || "We've Built This Before.\nHere's Proof.";
  const headingText = data?.text || "Real infrastructure. Shipped and live.";

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
      },
    },
  };

  const buttonVariants = {
    initial: { scale: 1 },
    hover: {
      scale: 1.05,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 10,
    },
    },
    tap: { scale: 0.95 },
  };

  return (
    <div id="projects" className="relative px-4 sm:px-6 md:px-12 min-[2560px]:px-16 pt-12 md:pt-20 min-[2560px]:pt-16 pb-20 min-[2560px]:pb-28 bg-common-section overflow-hidden">
      
      {/* Arched Heading Section */}
      <motion.div
        className="relative pt-20 pb-16 min-[1024px]:max-[1180px]:pt-14 min-[1024px]:max-[1180px]:pb-12 min-[2560px]:pt-24 min-[2560px]:pb-24 flex flex-col items-center justify-center text-center z-20"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <div className="relative z-10">
          <Heading
            title={headingTitle}
            subTitle={headingSubTitle}
            text={headingText}
            className="min-[2560px]:[&>div]:gap-5 min-[2560px]:[&>div>div:first-child]:text-[30px] min-[2560px]:[&_[data-subtitle]]:text-[76px] min-[2560px]:[&_[data-subtitle]]:leading-[1.1] min-[2560px]:[&>div>div:nth-child(3)]:text-[28px] min-[2560px]:[&>div>div:nth-child(3)]:max-w-[1300px]"
          />
        </div>
      </motion.div>

      {/* Grid of Projects & Elliptical Arc */}
      <div className="relative z-10 mx-auto max-w-[1400px] min-[2560px]:max-w-[2100px]">
        {/* Decorative ellipse image (replaces inline SVG) - moved visually to background for legibility */}
        <div
          className="absolute -top-[500px] min-[1024px]:max-[1180px]:-top-[430px] min-[2560px]:-top-[620px] left-1/2 -translate-x-1/2 w-[75%] min-[1024px]:max-[1180px]:w-[92%] min-[2560px]:w-[82%] max-w-[1600px] min-[2560px]:max-w-[2400px] pointer-events-none"
          style={{ zIndex: -10 }}
        >
          <img
            src={ELLIPSE}
            alt="decorative ellipse"
            className="w-full h-auto scale-y-[1]"
          />
        </div>

        <motion.div
          className="relative z-10"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 min-[1024px]:max-[1180px]:gap-8 min-[2560px]:gap-16 px-4 min-[2560px]:px-10">
            {(projectData?.ProjectsList || [])
              .slice(0, visibleCount)
              .map((project: any, index: number) => {
                const displayIndex = String(index + 1).padStart(2, '0');
                return (
                  <motion.div
                    key={index}
                    className="group relative flex flex-col md:flex-row items-center gap-6 min-[1024px]:max-[1180px]:gap-4 p-4 md:p-10 min-[1024px]:max-[1180px]:p-6 min-[2560px]:gap-10 min-[2560px]:p-14 rounded-[24px] min-[2560px]:rounded-[28px] bg-[#141414] transition-all duration-300 cursor-pointer overflow-hidden"
                    variants={itemVariants}
                    onClick={() => navigate(project.projectNavigate)}
                  >
                    {/* Left content */}                  
                      <div className="flex-1 flex flex-row md:flex-col items-start gap-3 md:gap-6 z-10 ">
                      <div className="hidden flex items-center justify-center h-16 w-16  min-[1024px]:max-[1180px]:h-14 min-[1024px]:max-[1180px]:w-14 min-[2560px]:h-20 min-[2560px]:w-20 rounded-full bg-white/5 border border-white/10 text-white font-bold text-xl md:text-2xl min-[1024px]:max-[1180px]:text-xl min-[2560px]:text-[34px] group-hover:bg-[#229CFF] group-hover:border-[#229CFF] transition-all duration-300">
                        {displayIndex}
                      </div>
                      <div className="flex flex-col gap-3">
                        <h3 className="text-2xl md:text-3xl min-[1024px]:max-[1180px]:text-[30px] min-[2560px]:text-[48px] font-bold text-white transition-colors duration-300">
                          {project.title}
                        </h3>
                        <p className="text-base min-[1024px]:max-[1180px]:text-[15px] min-[2560px]:text-[24px] text-gray-400 line-clamp-3 leading-relaxed min-[2560px]:leading-[1.5]">
                          {project.description || "Secure, easy-to-use crypto trading platform with 15k+ digital assets, spot & futures trading, copy trading, and strong AML/KYC security."}
                        </p>
                      </div>
                    </div>

                    {/* Right Mockup */}
                    <div className="relative w-[190px] h-[190px] min-[1024px]:max-[1180px]:w-[215px] min-[1024px]:max-[1180px]:h-[215px] lg:w-[285px] lg:h-[285px] min-[2560px]:w-[390px] min-[2560px]:h-[390px] border border-[#DFD072] rounded-xl min-[2560px]:rounded-2xl overflow-hidden bg-[#0D0D0D] flex items-center justify-center transition-all duration-500">
                      <img 
                        src={project.projectImage} 
                        alt={project.title}
                        className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-700" 
                      />
                    </div>
                  </motion.div>
                );
              })}
          </div>
        </motion.div>
      </div>

      {projectData?.ProjectsList?.length > 4 && (
        <motion.div
          className="flex items-center justify-center mt-10 lg:mt-20 min-[2560px]:mt-28"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <motion.div
            variants={buttonVariants}
            initial="initial"
            whileHover="hover"
            whileTap="tap"
          >
            <ButtonComponent
              className="rounded-full flex gap-2 items-center shadow-lg bg-yellow text-white px-6 lg:px-10 py-3 lg:py-5 text-lg"
              iconPath={RightArrow}
              labelClassname="text-base md:text-xl lg:text-2xl font-bold font-sans"
              label={"View More"}
              onClick={() => navigate("/portfolio")}
            />
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default ProjectDisplay;
